import { useNavigate } from 'react-router-dom';
import { AlertCircle, RefreshCw, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { StatusCard } from '~/components/StatusCard';
import { PaymentMethodSelector } from '~/components/PaymentMethodSelector';
import { PaymentInfoCard } from '~/components/PaymentInfoCard';
import { separateAndSortInvoices } from '~/utils/paymentUtils';

export function PaymentErrorState({
  paymentStatus: _paymentStatus,
  checkoutStatus,
  checkoutUrl,
  invoiceSummary,
  paymentSettings,
  currentPaymentMethod,
  originalPaymentMethod,
  onPaymentMethodChange,
  onStartNewPayment,
  creatingNewCheckout,
  error
}: any) {
  const navigate = useNavigate();
  const canResumeCheckout = checkoutStatus === 'open' && checkoutUrl;
  const sessionExpired = checkoutStatus === 'expired';
  const { tuitionInvoices, dueNowTotal } = separateAndSortInvoices(invoiceSummary);
  const acceptsOnlinePayments = paymentSettings?.acceptOnlinePayments;

  const validPaymentMethods = ['ach', 'card'];
  const isValidPaymentMethod = validPaymentMethods.includes(currentPaymentMethod);

  // Check if user changed the payment method
  const paymentMethodChanged = originalPaymentMethod && currentPaymentMethod !== originalPaymentMethod;

  // Get payment method label for button text
  const getPaymentMethodLabel = (method: string) => {
    return method === 'card' ? 'Card' : 'Bank';
  };

  // Determine title and icon based on state
  const title = canResumeCheckout
    ? 'Payment Incomplete'
    : sessionExpired
    ? 'Checkout Session Expired'
    : 'Payment Failed';

  const titleClassName = canResumeCheckout ? 'text-amber-600' : 'text-destructive';

  const icon = canResumeCheckout
    ? <RefreshCw className="h-5 w-5" />
    : <AlertCircle className="h-5 w-5" />;

  return (
    <StatusCard
      icon={icon}
      title={title}
      titleClassName={titleClassName}
      footer={
        <div className="flex gap-2 flex-wrap">
          {canResumeCheckout ? (
            <>
              <Button
                onClick={paymentMethodChanged ? onStartNewPayment : () => { window.location.href = checkoutUrl; }}
                disabled={creatingNewCheckout || !isValidPaymentMethod}
              >
                {creatingNewCheckout ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    {paymentMethodChanged
                      ? `Continue to Payment with ${getPaymentMethodLabel(currentPaymentMethod)}`
                      : 'Continue to Payment'
                    }
                  </>
                )}
              </Button>
            </>
          ) : sessionExpired ? (
            <Button onClick={onStartNewPayment} disabled={creatingNewCheckout || !isValidPaymentMethod}>
              {creatingNewCheckout ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Continue to Payment with {getPaymentMethodLabel(currentPaymentMethod)}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={onStartNewPayment}
              disabled={creatingNewCheckout || !isValidPaymentMethod}
            >
              {creatingNewCheckout ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Try Again with {getPaymentMethodLabel(currentPaymentMethod)}
                </>
              )}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/app')}>
            Contact School
          </Button>
        </div>
      }
    >
      {/* Status-specific messages */}
      {canResumeCheckout ? (
        <>
          <p className="text-muted-foreground">
            Your enrollment packet has been submitted, but payment was not completed.
            You can resume the checkout process or change your payment method.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              Your checkout session is still active. You can complete payment with the current method or change it below.
            </p>
          </div>
        </>
      ) : sessionExpired ? (
        <>
          <p className="text-muted-foreground">
            Your enrollment packet has been submitted, but the checkout session has expired.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Select a payment method below to start a new payment session and complete your enrollment.
            </p>
          </div>
        </>
      ) : (
        <>
          <p className="text-muted-foreground">
            {error || 'Your payment could not be processed. Please try again or contact the school office.'}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              You can select a different payment method below to try again.
            </p>
          </div>
        </>
      )}

      {/* Payment Method Selection */}
      {acceptsOnlinePayments && (
        <>
          <PaymentMethodSelector
            selectedMethod={currentPaymentMethod}
            originalMethod={originalPaymentMethod}
            onChange={onPaymentMethodChange}
            dueNowTotal={dueNowTotal}
            tuitionInvoices={tuitionInvoices}
            showFuturePaymentsInfo={false}
          />

          {/* Show recurring payment info if there are future invoices */}
          {tuitionInvoices.length > 0 && currentPaymentMethod && (
            <PaymentInfoCard
              paymentMethod={currentPaymentMethod}
              tuitionInvoices={tuitionInvoices}
              firstFuturePaymentDate={tuitionInvoices[0]?.dueDate || ''}
            />
          )}
        </>
      )}
    </StatusCard>
  );
}

export default PaymentErrorState;
